import { emailList, maxCharactersValidator, plainText, richText } from '../../../../../../modules/validators';
import { trimSpacesLeft } from '../../../../../../modules/formatters';
import { FormVmItemConfigType, FormVmValueValidatorResultType, InitialData } from '../../../../../../infrastructure/form-viewmodel-controller';
import { FlyoutTabOption, LayoutOption } from '../../../../../../api/connections.dao';
import { isBefore, isSameDay } from 'date-fns';
import { DESCRIPTION as DESCRIPTION_LABEL } from '../../../../../../constants/common-labels.constants';
import { buildConfigList } from '../../../../../../infrastructure/utils/form-config.utils';

export enum FieldKey {
    BROADCAST_DATE_TIME = 'broadcastDateTime',
    TOPIC = 'topic',
    DESCRIPTION = 'description',
    NEVER_LATE_NAVIGATION = 'neverLateNavigation',
    FLYOUT_TABS = 'flyoutTabs',
    LAYOUT = 'layout',
    QA_NOTIFICATION_EMAIL = 'qaNotificationEmailRecipients',
    STANDBY_MESSAGE = 'standByMessage',
    EXPIRATION_DATE_ENABLED = 'expirationDateEnabled',
    EXPIRATION_DATE = 'expirationDate',
}

export const topicMaxLengthValidator = (val: string): FormVmValueValidatorResultType => maxCharactersValidator(val, 250);

export const knovioDatesCompareValidator = (selectedDate: string, formValues: InitialData) => {
    const dateToCompare = formValues[FieldKey.BROADCAST_DATE_TIME];
    const convertedSelectedDate = selectedDate ? new Date(selectedDate) : null;
    const convertedDateToCompare = dateToCompare ? new Date(dateToCompare) : null;
    const nextDateFromCurrent = new Date();
    nextDateFromCurrent.setDate(nextDateFromCurrent.getDate() + 1);

    const isEarlierThanDateToCompare = isBefore(convertedSelectedDate, convertedDateToCompare);
    const isEarlierThanCurrentDate = isBefore(convertedSelectedDate, nextDateFromCurrent);
    const isSameDate = isSameDay(convertedSelectedDate, nextDateFromCurrent);

    let valid = true;
    if (convertedSelectedDate && convertedDateToCompare) {
        valid = !isEarlierThanDateToCompare && (isSameDate || !isEarlierThanCurrentDate);
    }

    const validationMessage = valid ? null : {
        id: `oe.error.expirationDatesCompare`,
        defaultMessage: 'Expiration date must be later than the current date and the broadcast date.'
    };

    return { valid: valid, validationMessage: validationMessage };
};

export const FORM_CONFIG = {
    [FieldKey.BROADCAST_DATE_TIME]: {
        labelDescriptor: { id: 'oe.knovioLiveStreamModal.broadcastDateTime', defaultMessage: 'Broadcast Date & Time' },
        defaultValue: ''
    },
    [FieldKey.TOPIC]: {
        labelDescriptor: { id: 'oe.knovioLiveStreamModal.topic', defaultMessage: 'Topic' },
        defaultValue: '',
        validators: [plainText.required, topicMaxLengthValidator],
        formatters: [trimSpacesLeft]
    },
    [FieldKey.DESCRIPTION]: {
        labelDescriptor: DESCRIPTION_LABEL,
        defaultValue: '',
        validators: [plainText.maxLength.medium],
        formatters: [trimSpacesLeft]
    },
    [FieldKey.NEVER_LATE_NAVIGATION]: {
        defaultValue: true,
    },
    [FieldKey.FLYOUT_TABS]: {
        labelDescriptor: { id: 'oe.knovioLiveStreamModal.flyoutTabs', defaultMessage: 'Flyout Tabs' },
        defaultValue: [FlyoutTabOption.Overview]
    },
    [FieldKey.LAYOUT]: {
        labelDescriptor: { id: 'oe.knovioLiveStreamModal.layout', defaultMessage: 'Layout' },
        defaultValue: LayoutOption.VideoRight
    },
    [FieldKey.QA_NOTIFICATION_EMAIL]: {
        labelDescriptor: { id: 'oe.knovioLiveStreamModal.qaNotificationEmailRecipients', defaultMessage: 'Q&A Notification Email Recipients' },
        defaultValue: '',
        validators: [plainText.maxLength.short, emailList.regExp]
    },
    [FieldKey.STANDBY_MESSAGE]: {
        labelDescriptor: { id: 'oe.knovioLiveStreamModal.standByMessage', defaultMessage: 'Standby Message' },
        defaultValue: 'Please stand by for the broadcast to begin…',
        validators: [richText.maxLength.long]
    },
    [FieldKey.EXPIRATION_DATE_ENABLED]: {
        defaultValue: false,
    },
    [FieldKey.EXPIRATION_DATE]: {
        labelDescriptor: { id: 'oe.knovioLiveStreamModal.expirationDate', defaultMessage: 'Expiration Date' },
        defaultValue: null,
        validators: [knovioDatesCompareValidator]
    }
};

export const configList: FormVmItemConfigType[] = buildConfigList(FORM_CONFIG);
